import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useLanguage } from '../App';
import { sessionAPI, simulationAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, MessageCircle, ScanFace } from 'lucide-react';
import { SessionList } from '../components/history/SessionList';
import { SimulationGallery } from '../components/history/SimulationGallery';

export default function HistoryPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'simulations' ? 'simulations' : 'conversations';
  const [sessions, setSessions] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSims, setLoadingSims] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadingSims(true);
    try {
      const res = await sessionAPI.list();
      setSessions(res.data.sessions || []);
    } catch (err) {
      setSessions([]);
    } finally {
      setLoading(false);
    }
    try {
      const res = await simulationAPI.list();
      setSimulations(res.data.simulations || []);
    } catch (err) {
      setSimulations([]);
    } finally {
      setLoadingSims(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openSession = (session) => {
    if (session.type === 'simulation') navigate(`/simulation/${session.session_id}`);
    else navigate(`/coach/${session.session_id}`);
  };

  const handleDelete = async (sessionId) => {
    try {
      await sessionAPI.delete(sessionId); // cascades to messages, analyses and simulation images
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      setSimulations(prev => prev.filter(s => s.session_id !== sessionId));
      toast.success(language === 'fr' ? 'Session supprim\u00e9e' : 'Session deleted');
    } catch (err) {
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting session');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filteredSessions = sessions.filter(s => s.title?.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="history-page">
      <div className="fade-in">
        <h1 className="font-display text-3xl text-white">{t('chatHistory', language)}</h1>
        <p className="text-white/50 text-sm mt-1">
          {language === 'fr' ? 'Retrouvez vos sessions et simulations pr\u00e9c\u00e9dentes' : 'Find your previous sessions and simulations'}
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearchParams(v === 'simulations' ? { tab: 'simulations' } : {})}>
        <TabsList className="bg-[var(--lux-panel)] border border-white/[0.08]">
          <TabsTrigger value="conversations" data-testid="history-tab-conversations" className="data-[state=active]:bg-[var(--lux-gold)] data-[state=active]:text-[var(--lux-ink)] text-white/60">
            <MessageCircle className="w-4 h-4 mr-2" /> {t('conversations', language)} ({sessions.length})
          </TabsTrigger>
          <TabsTrigger value="simulations" data-testid="history-tab-simulations" className="data-[state=active]:bg-[var(--lux-gold)] data-[state=active]:text-[var(--lux-ink)] text-white/60">
            <ScanFace className="w-4 h-4 mr-2" /> {t('simulations', language)} ({simulations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conversations" className="space-y-4 mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              data-testid="chat-history-search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('searchSessions', language)}
              className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          {loading ? (
            <div className="flex justify-center py-12" data-testid="history-loading">
              <div className="w-8 h-8 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <SessionList sessions={filteredSessions} language={language} onOpen={openSession} onDelete={setDeleteTarget} />
          )}
        </TabsContent>

        <TabsContent value="simulations" className="mt-4">
          <SimulationGallery
            simulations={simulations}
            loading={loadingSims}
            language={language}
            onOpen={(id) => navigate(`/simulation/${id}`)}
            onDelete={setDeleteTarget}
            onCompare={(a, b) => navigate(`/simulation/compare?a=${a}&b=${b}`)}
            onNew={() => navigate('/simulation')}
            retentionDays={user?.retention_days || 90}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="bg-[var(--lux-panel)] border-white/10" data-testid="delete-session-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{language === 'fr' ? 'Supprimer cette session ?' : 'Delete this session?'}</DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-sm">{language === 'fr' ? 'Cette action est irr\u00e9versible.' : 'This action cannot be undone.'}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="bg-[var(--lux-panel)] border-white/10 text-white" data-testid="delete-session-cancel">
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={() => handleDelete(deleteTarget)} className="bg-[var(--lux-danger)] text-white hover:bg-lux-danger/80" data-testid="delete-session-confirm">
              {t('deleteSession', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
